import { Component, computed, inject, signal, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { selectProjects } from '@zayan-erp/stores';
import { toSignal } from '@angular/core/rxjs-interop';
import { ConfirmHelperService, rxResourceFixed } from '@zayan-erp/shared';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { TableModule } from '@openng/optimus-ui/table';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import {
  DropdownData,
  GenericDropdownComponent,
  InputFieldComponent,
  CurrencyInputFieldComponent,
  CurrencyFormatPipe,
  LookupsService,
  ConfirmPopupComponent
} from '@zayan-erp/shared';
import { ProjectDepositScheduleService } from '../../services/project-deposit-schedule.service';
import { CreateDepositSchedulePayload, ProjectDepositScheduleRow } from '@zayan-erp/sales';

@Component({
  selector: 'lib-project-deposit-schedule',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    DatePickerModule,
    NgbDropdownModule,
    GenericDropdownComponent,
    InputFieldComponent,
    CurrencyInputFieldComponent,
    CurrencyFormatPipe,
    ConfirmPopupComponent
  ],
  templateUrl: './project-deposit-schedule.component.html'
})
export class ProjectDepositScheduleComponent {
  #fb = inject(FormBuilder);
  #store = inject(Store);
  #depositScheduleService = inject(ProjectDepositScheduleService);
  #lookupsService = inject(LookupsService);
  #confirmHelperService = inject(ConfirmHelperService);

  project$ = this.#store.select(selectProjects);
  projects = toSignal(this.project$, { initialValue: [] as DropdownData[] });
  tasks = toSignal(this.#lookupsService.readTasks(), { initialValue: [] as DropdownData[] });

  selectedProjectId = signal<string | null>(null);
  selectedRowId = signal<string | null>(null);
  isSaving = signal(false);
  // deleteDepositId = signal<string | null>(null);
  form: FormGroup = this.#fb.group({
    // taskId: [null],
    offsetBusinessDays: [0, [Validators.required, Validators.min(0)]],
    amount: [null as number | null, [Validators.required, Validators.min(0)]],
    sortOrder: [0]
  });

  /** Load only the rows of the deposit schedule for the selected project (for the grid). */
  depositsResource = rxResourceFixed({
    params: () => ({ projectId: this.selectedProjectId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.projectId) {
        return of([]);
      }
      return this.#depositScheduleService.getDepositsByProject(params.projectId).pipe(
        map(res => {
          // API returns array of schedules, each with .rows; flatten to one array of rows
          if (Array.isArray(res)) {
            return (res as { rows?: unknown[] }[]).flatMap(
              schedule => schedule?.rows ?? []
            ) as ProjectDepositScheduleRow[];
          }
          const rows = res?.rows;
          if (Array.isArray(rows)) {
            return rows as ProjectDepositScheduleRow[];
          }
          return [];
        })
      );
    }
  });

  deposits = computed(() => {
    console.log(this.depositsResource.value());
    return this.depositsResource.value() ?? [];
  });

  clearSelection(): void {
    this.selectedRowId.set(null);
    this.form.reset();
    this.isSaving.set(false);
  }

  onEditRow(row: ProjectDepositScheduleRow): void {
    this.selectedRowId.set(row.id);
    this.form.patchValue({
      taskId: row.taskId ?? '',
      offsetBusinessDays: row.offsetBusinessDays ?? 0,
      amount: row.amount ?? null,
      sortOrder: row.sortOrder ?? 0
    });
  }

  onSave(): void {
    if (this.form.invalid || !this.selectedProjectId()) {
      return;
    }
    const projectId = this.selectedProjectId()!;
    const value = this.form.getRawValue();
    const payload: CreateDepositSchedulePayload = {
      taskId: value.taskId,
      offsetBusinessDays: Number(value.offsetBusinessDays ?? 0),
      amount: value.amount ?? 0.01,
      sortOrder: Number(value.sortOrder ?? 0)
    };

    const editingId = this.selectedRowId();
    this.isSaving.set(true);

    if (editingId) {
      this.#depositScheduleService.updateDepositSchedule(editingId, payload).subscribe({
        next: () => {
          this.isSaving.set(false);
          this.depositsResource.reload();
        },
        complete: () => {
          this.clearSelection();
        }
      });
    } else {
      this.#depositScheduleService.createDepositSchedule(projectId, [payload]).subscribe({
        next: () => {
          this.isSaving.set(false);
          this.form.reset();
          this.depositsResource.reload();
        },
        complete: () => {
          this.clearSelection();
        }
      });
    }
  }

  getTaskLabel(taskId: string | null | undefined): string {
    if (!taskId) return '-';
    const task = this.tasks().find(t => t.id === taskId);
    return task?.secondLabel ?? task?.firstLabel ?? taskId;
  }

  confirmDepositDelete(deposit: ProjectDepositScheduleRow): void {
    this.selectedRowId.set(deposit.id);

    this.#depositScheduleService.deleteDepositSchedule(this.selectedRowId() ?? '').subscribe({
      next: () => this.depositsResource.reload(),
      complete: () => {
        this.clearSelection();
      }
    });
  }

  onConfirmDelete(data: any): void {
    if (!data) return;
    this.#depositScheduleService.deleteDepositSchedule(this.selectedRowId() ?? '').subscribe({
      next: () => this.depositsResource.reload(),
      complete: () => {
        this.clearSelection();
      }
    });
  }
}
